import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PasswordSetupForm } from "@/components/auth/PasswordSetupForm";
import { usePasswordSetup } from "@/hooks/usePasswordSetup";
import { useAuthVerification } from "@/hooks/useAuthVerification";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Capacitor } from "@capacitor/core";
import { Smartphone } from "lucide-react";
import { appFromSearchParams, readLinkParams, type AppBranding } from "@/config/appBranding";

type MobileOs = "ios" | "android" | null;

const detectMobileBrowserOs = (): MobileOs => {
  if (Capacitor.isNativePlatform() || typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  return null;
};

/** Lien qui ouvre précisément l'app de la salle, avec les mêmes paramètres. */
const buildOpenInAppUrl = (branding: AppBranding, os: MobileOs, search: string): string => {
  const pathAndQuery = `set-password${search}`;
  if (os === "android") {
    const fallback = encodeURIComponent(window.location.href);
    return `intent://${pathAndQuery}#Intent;scheme=${branding.deepLinkShortcut};package=${branding.androidPackage};S.browser_fallback_url=${fallback};end`;
  }
  return `${branding.deepLinkShortcut}://${pathAndQuery}`;
};

const SetPasswordPage = () => {
  const { t } = useTranslation("appui");
  const navigate = useNavigate();
  const location = useLocation();
  const linkParams = useMemo(
    () => readLinkParams(location.search, location.hash),
    [location.search, location.hash]
  );
  const branding = appFromSearchParams(linkParams);
  const appNameFromUrl = branding.key;
  const mobileOs = useMemo(detectMobileBrowserOs, []);
  const openInAppUrl = mobileOs
    ? buildOpenInAppUrl(branding, mobileOs, `?${linkParams.toString()}`)
    : null;

  const { loading, isRecoveryMode, resetToken, linkError } = useAuthVerification();
  const { isSubmitting, handlePasswordUpdate } = usePasswordSetup(appNameFromUrl, resetToken);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <p>{t("setPassword.loading")}</p>
      </div>
    );
  }

  const title = isRecoveryMode
    ? t("setPassword.recoveryTitle")
    : t("setPassword.setTitle");

  const description = isRecoveryMode
    ? t("setPassword.recoveryDescription")
    : t("setPassword.setDescription");

  const invalidDescription =
    linkError === "expired"
      ? t("setPassword.invalidLinkExpired")
      : linkError === "already_used"
        ? t("setPassword.invalidLinkUsed")
        : t("setPassword.invalidLinkGeneric");

  return (
    <div className="min-h-screen flex items-center justify-center p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1rem)] bg-muted">
      <div className="w-full max-w-md space-y-4">
      <img
        src={branding.logoDarkLightUrl}
        alt={branding.longName}
        className="mx-auto h-16 w-auto object-contain"
      />
      {openInAppUrl && !linkError && (
        <Button asChild variant="outline" className="w-full">
          <a href={openInAppUrl}>
            <Smartphone className="mr-2 h-4 w-4" />
            {t("setPassword.openInApp", { app: branding.longName })}
          </a>
        </Button>
      )}
      <Card className="w-full shadow-lg">
        {linkError ? (
          <>
            <CardHeader>
              <CardTitle>{t("setPassword.invalidLinkTitle")}</CardTitle>
              <CardDescription>{invalidDescription}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                onClick={() => navigate(`/forgot-password?appName=${encodeURIComponent(appNameFromUrl)}`)}
              >
                {t("setPassword.requestNewLink")}
              </Button>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
              <PasswordSetupForm
                onSubmit={handlePasswordUpdate}
                isSubmitting={isSubmitting}
                isRecoveryMode={isRecoveryMode}
              />
            </CardContent>
          </>
        )}
      </Card>
      </div>
    </div>
  );
};

export default SetPasswordPage;
